import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export type AccessState = 'loading' | 'guest' | 'pending' | 'trial' | 'trial_ended' | 'active' | 'blocked';

export const useMembershipStatus = () => {
  const { user, loading: authLoading } = useAuth();
  const [accessState, setAccessState] = useState<AccessState>('loading');

  useEffect(() => {
    if (authLoading) {
      setAccessState('loading');
      return;
    }

    if (!user) {
      setAccessState('guest');
      return;
    }

    const fetchStatus = async () => {
      // Fetch membership and profile in parallel
      const [membershipRes, profileRes] = await Promise.all([
        supabase
          .from('membership_applications')
          .select('status')
          .eq('user_id', user.id)
          .maybeSingle(),
        supabase
          .from('profiles')
          .select('is_subscribed, trial_requests_used, trial_start_date')
          .eq('id', user.id)
          .single(),
      ]);

      if (membershipRes.error) {
        console.error('Error fetching membership status:', membershipRes.error);
        setAccessState('guest');
        return;
      }

      const membership = membershipRes.data;
      const profile = profileRes.data;

      if (!membership) {
        setAccessState('guest');
        return;
      }

      const status = membership.status;

      if (status === 'pending') {
        setAccessState('pending');
        return;
      }

      if (status === 'blocked') {
        setAccessState('blocked');
        return;
      }

      // For "active" membership, compute effective status from profile
      if (status === 'active' && profile) {
        if (profile.is_subscribed) {
          setAccessState('active');
          return;
        }

        // Trial user — check limits
        const trialRequestsUsed = profile.trial_requests_used ?? 0;
        const trialStartDate = profile.trial_start_date ? new Date(profile.trial_start_date) : null;
        const trialDaysElapsed = trialStartDate
          ? Math.floor((Date.now() - trialStartDate.getTime()) / (1000 * 60 * 60 * 24))
          : 0;

        if (trialRequestsUsed >= 10 || trialDaysElapsed >= 7) {
          setAccessState('trial_ended');
        } else {
          setAccessState('trial');
        }
        return;
      }

      // Fallback for any other status value (e.g. "trial", "trial_ended" stored directly)
      if (['trial', 'trial_ended'].includes(status)) {
        setAccessState(status as AccessState);
        return;
      }

      setAccessState('guest');
    };

    fetchStatus();
  }, [user, authLoading]);

  return { accessState };
};
